<script lang="ts">
  import { onMount, onDestroy } from 'svelte';
  import { presentationStore, formattedTime } from '../../stores/presentationStore';
  import { gameStore } from '../../stores/gameStore';
  import { soundService } from '../../services/soundService';
  import { SoundCategory } from '../../types/audio';
  import PresenterView from './PresenterView.svelte';
  import GuesserView from './GuesserView.svelte';

  $: isPresenter = $presentationStore.isPresenter;
  $: timerEndTime = $gameStore.gameState?.timerEndTime;
  $: remainingTime = $presentationStore.remainingTime;

  let timerInterval: ReturnType<typeof setInterval>;
  let timerWarningPlayed = false;
  let timerTickId: string | null = null;

  onMount(() => {
    // Update timer every 100ms
    timerInterval = setInterval(() => {
      if (timerEndTime) {
        const remaining = Math.max(0, timerEndTime - Date.now());
        presentationStore.updateRemainingTime(remaining);
      }
    }, 100);
  });

  onDestroy(() => {
    if (timerInterval) clearInterval(timerInterval);

    // Stop timer tick sound if playing
    if (timerTickId) {
      soundService.stop(timerTickId);
      timerTickId = null;
    }
  });

  // Timer warning at 30 seconds (play once)
  $: if (remainingTime <= 30000 && remainingTime > 29000 && !timerWarningPlayed) {
    timerWarningPlayed = true;
    soundService.play('/audio/ui/notification.ogg', {
      category: SoundCategory.UI,
      volume: 0.9,
    });
  }

  // Timer ticking at 10 seconds (loop until component destroyed or timer ends)
  $: if (remainingTime <= 10000 && remainingTime > 0 && !timerTickId) {
    soundService.play('/audio/ambient/timer-tick.ogg', {
      category: SoundCategory.AMBIENT,
      loop: true,
      volume: 0.5,
    }).then((id) => {
      if (id) timerTickId = id;
    });
  }

  // Stop timer tick when timer runs out
  $: if (remainingTime === 0 && timerTickId) {
    soundService.stop(timerTickId);
    timerTickId = null;
  }

  // Calculate progress percentage (0-100)
  $: progressPercent = timerEndTime
    ? Math.max(0, Math.min(100, (remainingTime / 150000) * 100))
    : 0;

  // Determine color based on remaining time
  $: timerColor = remainingTime > 60000
    ? '#10b981' // green
    : remainingTime > 30000
    ? '#f59e0b' // amber
    : '#ef4444'; // red
</script>

<div class="presentation-view">
  <div class="timer-bar" role="timer">
    <div class="timer-text">{$formattedTime}</div>
    <div
      class="timer-progress"
      style="width: {progressPercent}%; background-color: {timerColor};"
    ></div>
  </div>

  <div class="content">
    {#if isPresenter}
      <PresenterView />
    {:else}
      <GuesserView />
    {/if}
  </div>
</div>

<style>
  .presentation-view {
    min-height: 100vh;
    background: #f9fafb;
  }

  .timer-bar {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    height: 4rem;
    background: white;
    border-bottom: 2px solid #e5e7eb;
    z-index: 100;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .timer-text {
    font-size: 1.5rem;
    font-weight: 700;
    color: #1f2937;
    z-index: 1;
  }

  .timer-progress {
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    opacity: 0.2;
    transition: width 0.1s linear, background-color 0.3s ease;
  }

  .content {
    padding-top: 4rem;
  }
</style>
